import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Plus, FileText, Search, Filter, ArrowLeft, Clock, CheckCircle, AlertTriangle, Hash, GitBranch } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { formatDate } from '../../utils/dateUtils';
import TabNavigation from '../../components/TabNavigation';
import DocumentNumberingSettings from './DocumentNumberingSettings';
import WorkflowSettings from './WorkflowSettings';

// Roles allowed to manage document configuration (numbering + workflow).
// Mirrors the backend _DOC_CONFIG_ROLES guard: administrators, the HSE Manager,
// and the Safety Manager, so buttons aren't shown to users who'd get a 403.
const DOC_CONFIG_ROLES = [
  'admin', 'Admin', 'super_admin', 'Super Admin', 'tenant_admin', 'Tenant Admin',
  'hse_manager', 'HSE Manager', 'safety_manager', 'Safety Manager',
];

const DocumentList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeSiteId } = useSite();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [filters, setFilters] = useState({
    documentType: '',
    status: '',
    department: '',
    keyword: ''
  });
  const [numberingOpen, setNumberingOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const canManageDocConfig = DOC_CONFIG_ROLES.includes(user?.role);
  // Derived once so the desktop table and the mobile card stack always render
  // the same page of results and the same empty-state wording.
  const pagedDocs = filteredDocs.slice((page - 1) * pageSize, page * pageSize);
  const emptyDocsMessage =
    (filters.keyword || filters.documentType || filters.status || filters.department)
      ? 'No documents match your filters. Try adjusting or clearing them.'
      : 'No documents found. Create your first document to get started.';

  useEffect(() => {
    fetchDocuments();
  }, [activeSiteId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    applyFilters();
  }, [documents, filters]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const siteParam = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents${siteParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    if (filters.documentType) {
      filtered = filtered.filter(doc => doc.document_type === filters.documentType);
    }
    if (filters.status) {
      filtered = filtered.filter(doc => doc.current_status === filters.status);
    }
    if (filters.department) {
      filtered = filtered.filter(doc => doc.department === filters.department);
    }
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(keyword) ||
        doc.document_number.toLowerCase().includes(keyword) ||
        doc.description?.toLowerCase().includes(keyword)
      );
    }

    setFilteredDocs(filtered);
    setPage(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Under_Review': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Published': 'bg-blue-100 text-blue-700',
      'Archived': 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    if (status === 'Published') return <CheckCircle className="w-4 h-4" />;
    if (status === 'Under_Review') return <Clock className="w-4 h-4" />;
    if (status === 'Archived') return <AlertTriangle className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/safety')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Document Control</h1>
              <p className="text-gray-600 mt-1">Manage SOPs, policies, and procedures</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canManageDocConfig && (
                <Button
                  variant="outline"
                  onClick={() => setWorkflowOpen(true)}
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Workflow
                </Button>
              )}
              {canManageDocConfig && (
                <Button
                  variant="outline"
                  onClick={() => setNumberingOpen(true)}
                >
                  <Hash className="w-4 h-4 mr-2" />
                  Numbering
                </Button>
              )}
              {hasPermission('documents', 'create') && (
                <Button
                  onClick={() => navigate('/documents/create')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Document
                </Button>
              )}
            </div>
          </div>
        </div>

        <DocumentNumberingSettings open={numberingOpen} onClose={() => setNumberingOpen(false)} />
        <WorkflowSettings open={workflowOpen} onClose={() => setWorkflowOpen(false)} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={filters.keyword}
                onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Document Type */}
            <select
              value={filters.documentType}
              onChange={(e) => setFilters({ ...filters, documentType: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="SOP">SOP</option>
              <option value="Work_Instruction">Work Instruction</option>
              <option value="Policy">Policy</option>
              <option value="Form">Form</option>
              <option value="Checklist">Checklist</option>
              <option value="Manual">Manual</option>
              <option value="SDS">SDS (Safety Data Sheet)</option>
              <option value="Other">Other</option>
            </select>

            {/* Status */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Under_Review">Under Review</option>
              <option value="Approved">Approved</option>
              <option value="Published">Published</option>
              <option value="Archived">Archived</option>
            </select>

            {/* Department */}
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {(filters.keyword || filters.documentType || filters.status || filters.department) && (
            <button
              onClick={() => setFilters({ documentType: '', status: '', department: '', keyword: '' })}
              className="mt-4 text-sm text-blue-600 hover:text-blue-700"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold text-green-700">
                  {documents.filter(d => d.current_status === 'Published').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Under Review</p>
                <p className="text-2xl font-bold text-yellow-700">
                  {documents.filter(d => d.current_status === 'Under_Review').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Draft</p>
                <p className="text-2xl font-bold text-gray-700">
                  {documents.filter(d => d.current_status === 'Draft').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-gray-600" />
            </div>
          </div>
        </div>

        {/* Documents Table (desktop) / card stack (mobile) */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {emptyDocsMessage}
                  </td>
                </tr>
              ) : (
                pagedDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/documents/${doc.id}`)}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                        <div className="text-sm text-gray-500">{doc.document_number}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{doc.document_type.replace('_', ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{doc.department || '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono text-gray-900">{doc.current_version}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(doc.current_status)}`}>
                        {getStatusIcon(doc.current_status)}
                        {doc.current_status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(doc.updated_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/documents/${doc.id}`);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          {/* Mobile card stack — same rows, no horizontal scrolling */}
          <div className="md:hidden p-4 space-y-4">
            {pagedDocs.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">{emptyDocsMessage}</div>
            ) : (
              pagedDocs.map((doc) => (
                <button
                  type="button"
                  key={doc.id}
                  onClick={() => navigate(`/documents/${doc.id}`)}
                  className="w-full text-left bg-gray-50 rounded-lg p-4 space-y-3"
                >
                  <div>
                    <div className="text-base font-semibold text-gray-900">{doc.title}</div>
                    <div className="text-sm text-gray-500 font-mono">{doc.document_number}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(doc.current_status)}`}>
                      {getStatusIcon(doc.current_status)}
                      {doc.current_status.replace('_', ' ')}
                    </span>
                    <span className="text-xs font-mono text-gray-600">{doc.current_version}</span>
                  </div>
                  <div className="text-sm text-gray-700 space-y-0.5">
                    <div><span className="font-semibold">Type:</span> {doc.document_type.replace('_', ' ')}</div>
                    <div><span className="font-semibold">Department:</span> {doc.department || '—'}</div>
                    <div><span className="font-semibold">Last Updated:</span> {formatDate(doc.updated_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        {filteredDocs.length > pageSize && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-sm text-gray-600">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredDocs.length)} of {filteredDocs.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {Math.ceil(filteredDocs.length / pageSize)}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(Math.ceil(filteredDocs.length / pageSize), p + 1))}
                disabled={page >= Math.ceil(filteredDocs.length / pageSize)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default DocumentList;
